import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PageContainer from '../../components/layout/PageContainer';
import PageField from '../../components/layout/PageField';
import { approachSteps } from '../../content/site';

const expectations = [
  'A shared picture of the system, the daily work, and where it gets in the way.',
  'The service and the first release are named before a long build starts.',
  'Working software your team can use in that environment before the engagement ends.',
  'Your team can run and release what we built, and review any AI step without us.',
];

type MarkColors = {
  blue: string;
  red: string;
  muted: string;
  surface: string;
};

const svgProps = {
  width: 160,
  height: 120,
  viewBox: '0 0 160 120',
  fill: 'none',
  xmlns: 'http://www.w3.org/2000/svg',
};

/** Small brand diagrams. The step heading already names the stage. */
const StepDiagram = ({ index, colors }: { index: number; colors: MarkColors }) => {
  const { blue, red, muted, surface } = colors;

  if (index === 0) {
    return (
      <svg {...svgProps}>
        <rect x="14" y="18" width="96" height="76" rx="8" stroke={blue} strokeWidth="2" />
        <path d="M14 36h96" stroke={blue} strokeWidth="2" />
        <circle cx="26" cy="27" r="2.5" fill={red} />
        <path d="M28 52h58M28 64h44M28 76h34" stroke={muted} strokeWidth="2" strokeLinecap="round" />
        <circle cx="112" cy="78" r="20" stroke={red} strokeWidth="2" />
        <path d="M126 92l16 16" stroke={red} strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (index === 1) {
    return (
      <svg {...svgProps}>
        <rect x="22" y="16" width="116" height="22" rx="5" stroke={muted} strokeWidth="2" />
        <path d="M30 27h28" stroke={red} strokeWidth="2" strokeLinecap="round" />
        <rect x="22" y="48" width="116" height="26" rx="5" fill={blue} />
        <rect x="22" y="84" width="116" height="22" rx="5" stroke={muted} strokeWidth="2" />
        <path d="M30 95h28" stroke={red} strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (index === 2) {
    return (
      <svg {...svgProps}>
        <rect x="40" y="10" width="100" height="68" rx="8" stroke={muted} strokeWidth="2" />
        <rect x="26" y="24" width="100" height="68" rx="8" fill={surface} stroke={blue} strokeWidth="2" />
        <rect x="12" y="38" width="100" height="68" rx="8" fill={surface} stroke={blue} strokeWidth="2" />
        <path d="M28 64h56M28 78h40" stroke={muted} strokeWidth="2" strokeLinecap="round" />
        <path d="M28 92h24" stroke={red} strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg {...svgProps}>
      <rect x="10" y="30" width="48" height="60" rx="8" stroke={blue} strokeWidth="2" />
      <path d="M22 50h24M22 62h16" stroke={muted} strokeWidth="2" strokeLinecap="round" />
      <path d="M70 60h28" stroke={red} strokeWidth="2" strokeLinecap="round" />
      <path d="M90 50l10 10-10 10" stroke={red} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="108" y="30" width="42" height="60" rx="8" fill={blue} />
      <path d="M120 62l6 6 12-14" stroke={surface} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const Approach = () => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const colors: MarkColors = {
    blue: dark ? '#3ea6ff' : '#0066b2',
    red: dark ? '#ff3d5c' : '#dc052d',
    muted: dark ? 'rgba(197, 212, 228, 0.55)' : 'rgba(16, 32, 51, 0.35)',
    surface: dark ? '#122033' : '#ffffff',
  };
  const wash = dark
    ? 'radial-gradient(ellipse 90% 80% at 40% 40%, rgba(62, 166, 255, 0.2), transparent 70%)'
    : 'radial-gradient(ellipse 90% 80% at 40% 40%, rgba(0, 102, 178, 0.12), transparent 72%)';
  const grid = dark
    ? 'linear-gradient(rgba(62, 166, 255, 0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(62, 166, 255, 0.16) 1px, transparent 1px)'
    : 'linear-gradient(rgba(0, 102, 178, 0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 102, 178, 0.12) 1px, transparent 1px)';

  return (
    <>
      <PageField>
        <PageContainer>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
            Approach
          </Typography>
          <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.25rem' }, maxWidth: 760, mt: 1 }}>
            How an engagement runs
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 680, fontSize: '1.125rem', lineHeight: 1.6, mt: 2 }}>
            Each engagement is one of four: custom software development, application modernization, AI workflow implementation, or prototype to production. We start from the system you run and the work around it.
          </Typography>
        </PageContainer>
      </PageField>
      <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {approachSteps.map((step, index) => {
          const odd = index % 2 === 0;
          const last = index === approachSteps.length - 1;
          return (
            <Box
              key={step.title}
              component="li"
              sx={{
                bgcolor: odd ? 'background.paper' : 'background.default',
                borderTop: 1,
                borderBottom: last ? 1 : 0,
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: 'min(1120px, calc(100% - 2.5rem))',
                  mx: 'auto',
                  py: { xs: 4, md: 5 },
                  display: 'grid',
                  gridTemplateColumns: { xs: '44px minmax(0, 1fr)', md: '44px 180px minmax(0, 640px)' },
                  columnGap: { xs: 2, md: 4 },
                  rowGap: { xs: 2, md: 0 },
                  alignItems: 'center',
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    left: 21,
                    width: 2,
                    bgcolor: 'primary.main',
                    zIndex: 0,
                    top: index === 0 ? '50%' : 0,
                    bottom: last ? '50%' : 0,
                  }}
                />
                <Box
                  aria-hidden
                  sx={{
                    gridColumn: 1,
                    gridRow: { xs: '1 / -1', md: 1 },
                    alignSelf: 'center',
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {String(index + 1).padStart(2, '0')}
                </Box>
                <Box
                  aria-hidden
                  sx={{
                    gridColumn: 2,
                    gridRow: 1,
                    justifySelf: 'start',
                    position: 'relative',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: 1,
                    borderColor: 'divider',
                    lineHeight: 0,
                    ...(odd
                      ? { bgcolor: 'transparent' }
                      : {
                          backgroundImage: grid,
                          backgroundSize: '28px 28px',
                        }),
                  }}
                >
                  {!odd && (
                    <Box
                      sx={{
                        pointerEvents: 'none',
                        position: 'absolute',
                        inset: 0,
                        background: wash,
                      }}
                    />
                  )}
                  <Box sx={{ position: 'relative' }}>
                    <StepDiagram index={index} colors={colors} />
                  </Box>
                </Box>
                <Box
                  sx={{
                    gridColumn: { xs: 2, md: 3 },
                    gridRow: { xs: 2, md: 1 },
                    maxWidth: 640,
                    minWidth: 0,
                    ...(!odd && {
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      px: { xs: 2.5, md: 3 },
                      py: { xs: 2.5, md: 3 },
                    }),
                  }}
                >
                  <Typography variant="h2" sx={{ fontSize: { xs: '1.6rem', md: '2rem' } }}>
                    {step.title}
                  </Typography>
                  <Typography sx={{ mt: 1.5, lineHeight: 1.7 }}>{step.text}</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.7 }}>
                    {step.detail}
                  </Typography>
                  <Box sx={{ mt: 2.5, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography
                      color="primary"
                      sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}
                    >
                      This step ends with
                    </Typography>
                    <Typography sx={{ mt: 0.75, lineHeight: 1.6 }}>{expectations[index]}</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
      <PageContainer>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <Button component={Link} to="/services" variant="outlined">
            See the services
          </Button>
          <Button component={Link} to="/contact" variant="contained">
            Talk about a project
          </Button>
        </Box>
      </PageContainer>
    </>
  );
};

export default Approach;
